import type { RadioButtonGroupProps } from '@hover/blueprint';
import { Box, RadioButton, RadioButtonGroup, motion } from '@hover/blueprint';
import type { IconProp } from '@hover/icons';
import {
  iconBath,
  iconBed,
  iconFurniture,
  iconOffice,
  iconResidential,
  iconSink,
  iconTable,
} from '@hover/icons';
import { splitAt } from 'ramda';
import React from 'react';

import {
  Selector,
  SelectorItem,
} from 'src/shared/components/Selector/Selector';
import { buttonPressed } from 'src/shared/telemetry';
import { hideScrollbars } from 'src/shared/utils/styles';

import { useHomeownerAppContext } from '../../hooks';
import { formatSpaceType } from '../Utils';

const ICONS: {
  [key: string]: IconProp;
} = {
  exterior: iconResidential,
  bedroom: iconBed,
  bathroom: iconBath,
  dining_room: iconTable,
  home_office: iconOffice,
  kitchen: iconSink,
  living_room: iconFurniture,
};

export interface SpaceTypeSelectorProps
  extends Omit<RadioButtonGroupProps, 'children'> {
  spaceType: string;
  spaceTypes?: string[];
  setSpaceType: (type: string) => void;
}

const MotionHiddenScrollBox = motion(hideScrollbars(Box));

const ScrollableSpaceTypeSelector = ({
  setSpaceType,
  spaceTypes,
  spaceType,
}: SpaceTypeSelectorProps) => {
  if (!spaceTypes) return null;

  const [firstHalf, secondHalf] =
    spaceTypes.length > 3
      ? splitAt(Math.ceil(spaceTypes.length / 2), spaceTypes)
      : [spaceTypes, []];

  return (
    <MotionHiddenScrollBox
      alignItems="start"
      flexDirection="column"
      height="100%"
      justifyContent={{
        tablet: 'center',
        base: 'left',
      }}
      overflowX="scroll"
      pb={{ base: 200, tablet: 700 }}
      position="relative"
      pt={{ base: 200, tablet: 200 }}
      width="100%"
    >
      <Selector
        alignItems="center"
        justifyContent="left"
        mb={500}
        onChange={setSpaceType}
        paddingInlineStart={0}
        value={spaceType}
        variant="secondary"
      >
        {firstHalf?.map(type => (
          <SelectorItem iconBefore={ICONS[type]} key={type} value={type}>
            {formatSpaceType(type)}
          </SelectorItem>
        ))}
      </Selector>
      {secondHalf.length > 0 && (
        <Selector
          alignItems="center"
          justifyContent="left"
          onChange={setSpaceType}
          paddingInlineStart={0}
          value={spaceType}
          variant="secondary"
        >
          {secondHalf?.map(type => (
            <SelectorItem iconBefore={ICONS[type]} key={type} value={type}>
              {formatSpaceType(type)}
            </SelectorItem>
          ))}
        </Selector>
      )}
    </MotionHiddenScrollBox>
  );
};
const SpaceTypeSelector = ({
  spaceType,
  spaceTypes = [],
  setSpaceType,
  onChange,
  ...props
}: SpaceTypeSelectorProps) => {
  const { trackingData } = useHomeownerAppContext();
  const displaySpaceTypes = spaceTypes;

  return (
    <RadioButtonGroup
      data-testid="space-type-selector"
      display="flex"
      flexDirection="row"
      flexWrap="wrap"
      gap={{ base: 200, tablet: 400, desktop: 200 }}
      justifyContent={{
        base: 'flex-start',
        tablet: 'center',
        desktop: 'flex-start',
      }}
      onChange={type => {
        buttonPressed({
          ...trackingData,
          track_id: 'upload-image-space-type-selection',
          button_location: 'middle-main-page',
          button_text: type,
        });

        setSpaceType(type);
        onChange?.(type);
      }}
      value={spaceType}
      {...props}
    >
      {displaySpaceTypes.map(type => (
        <RadioButton
          gap={200}
          iconBefore={ICONS[type]}
          key={type}
          outlineOffset="-6px !important"
          size={{ base: 'medium', tablet: 'large' }}
          value={type}
        >
          {formatSpaceType(type)}
        </RadioButton>
      ))}
      {displaySpaceTypes.length === 0 && (
        <RadioButton isDisabled isLoading width={600} />
      )}
    </RadioButtonGroup>
  );
};

export { SpaceTypeSelector, ScrollableSpaceTypeSelector };
